import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FolderCard, IFolderItem } from './components/list-item';
import CreateOrEditModal from './components/create-or-edit-modal';
import Modal from '../../components/modals/modal';
import { useService } from '../../services/services';
import css from './index.module.css';
import AddFile from './add-file';
import { Subject } from 'rxjs';
import OptionModal from '../../components/modals/option-modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { can } from '../../services/utils';

const CreateFolder = ({ folders, setFolders }) => {
  const { t } = useTranslation();
  const { documentStorageService } = useService();
  const { farmId, tab } = useParams<{ [key: string]: string }>();
  const [selected, setSelected] = useState<IFolderItem>(null);
  const [editFolder, setEditFolder] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));
  const canCreate = can(farmUser, { title: `create document_storage_${tab}` });
  const canEdit = can(farmUser, { title: `update document_storage_${tab}` });
  const canDelete = can(farmUser, { title: `delete document_storage_${tab}` });

  const handleToggleModal = (type?: string) => {
    if (type === 'delete') {
      setOpenDeleteModal((prev) => !prev);
    } else if (type === 'create' || type === 'edit') {
      setOpenModal((prev) => !prev);
    } else {
      setOpenDeleteModal(false);
      setOpenModal(false);
    }
  };

  const handleDeleteFolder = async (name: string) => {
    try {
      const response = await documentStorageService
        .deleteFolder(farmId, tab, name)
        .toPromise();
      setFolders(response.folders.folders);
      setEditFolder(null);
      handleToggleModal('delete');
    } catch (error) {
      console.error('Error deleting folder:', error);
    } finally {
      setShowFileUploadModal(false);
    }
  };

  const handleOnSubmit = async (data: { name: string }) => {
    const progressSubject = new Subject<ProgressEvent>();
    let response: { folders: any };
    if (editFolder) {
      response = await documentStorageService
        .editFolder(farmId, tab, editFolder.name, data.name)
        .toPromise();
    } else {
      response = await documentStorageService
        .store(farmId, tab, data.name, progressSubject)
        .toPromise();
    }
    setFolders(response.folders.folders);
    setEditFolder(null);
    setOpenModal(false);
    setShowFileUploadModal(false);
  };

  const handleCancelDeleteFolder = () => {
    setEditFolder(null);
    handleToggleModal('delete');
  };

  const fetchFolders = useCallback(async () => {
    try {
      const response = await documentStorageService
        .getFolders(farmId, tab)
        .toPromise();
      setFolders(response.folders.folders);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  }, [documentStorageService, farmId, tab, setFolders]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const modalContent = openDeleteModal ? (
    <OptionModal
      title={t('pages.document_storage.delete_folder_title')}
      className={css['delete-modal-wrapper']}
      buttons={[
        { title: t('Yes'), name: 'yes', value: true, ignoreValidation: true },
        { title: t('No'), name: 'no', value: false, ignoreValidation: true },
      ]}
      onChoice={(choice) => {
        if (choice) {
          handleDeleteFolder(editFolder.name);
        } else {
          handleCancelDeleteFolder();
        }
      }}
      onClose={() => handleCancelDeleteFolder()}
    />
  ) : (
    <CreateOrEditModal
      onClose={() => {
        handleToggleModal();
        setEditFolder(null);
      }}
      value={editFolder}
      items={folders}
      editItem={editFolder}
      handleOnSubmit={handleOnSubmit}
      title={
        editFolder
          ? t('pages.document_storage.rename_folder')
          : t('pages.document_storage.add_new_folder')
      }
      label={t('pages.document_storage.folder_name')}
      submitTitle={editFolder ? t('edit') : t('common.create')}
    />
  );

  return (
    <div className={css['container']}>
      <div className={css['list-container']}>
        {canCreate && (
          <button
            className={css['add-folder-btn']}
            onClick={() => handleToggleModal('create')}
          >
            {t('pages.document_storage.add_new_folder')}
          </button>
        )}
        {folders?.map((item, idx) => (
          <FolderCard
            key={`${item.name}-${idx}`}
            item={item}
            selected={selected}
            setSelected={setSelected}
            setEditFolder={setEditFolder}
            handleModal={handleToggleModal}
            setShowFileUploadModal={setShowFileUploadModal}
            canDelete={canDelete}
            canEdit={canEdit}
            canCreate={canCreate}
          />
        ))}

        <Modal
          visible={openModal || openDeleteModal}
          onClose={handleToggleModal}
          body={modalContent}
          buttons={null}
        />
      </div>
      {showFileUploadModal && (
        <AddFile
          item={selected}
          onFolderUpdate={(updatedFolder) => {
            setFolders((prevFolders) =>
              prevFolders.map((folder) =>
                folder.name === updatedFolder.name ? updatedFolder : folder
              )
            );
          }}
        />
      )}
    </div>
  );
};

export default CreateFolder;
